import { useRouter } from '@uirouter/react';

import { Environment } from '@/react/portainer/environments/types';
import { EnvironmentGroup } from '@/react/portainer/environments/environment-groups/types';
import { notifyError, notifySuccess } from '@/portainer/services/notifications';
import { AccessDatatable } from '@/react/portainer/access-control/AccessManagement/AccessDatatable/AccessDatatable';
import {
  TeamAccessViewModel,
  UserAccessViewModel,
} from '@/portainer/models/access';

import { Alert } from '@@/Alert';

import { useAccesses } from '../queries/useAccesses';
import { useUpdateEnvironmentAccess } from '../queries/useUpdateEnvironmentAccess';
import { PolicyType } from '../../policies/types';

interface Props {
  environment?: Environment;
  group?: EnvironmentGroup;
  minimumAgentVersions?: Record<PolicyType, string>;
}

export function AccessDatatableWrapper({
  environment,
  group,
  minimumAgentVersions,
}: Props) {
  const router = useRouter();
  const {
    authorizedUsersAndTeams,
    policyID,
    policyName,
    hasPolicyOverride,
    isLoading,
    error,
  } = useAccesses(environment, group, minimumAgentVersions);
  const updateMutation = useUpdateEnvironmentAccess();

  if (!authorizedUsersAndTeams) {
    return null;
  }

  if (error) {
    return (
      <div className="row">
        <div className="col-sm-12">
          <Alert color="error" title="Error loading access">
            {error.message ?? 'Unknown error'}
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <AccessDatatable
      tableKey="access_endpoint"
      dataset={authorizedUsersAndTeams}
      onRemove={handleRemove}
      onUpdate={handleUpdate}
      showWarning
      isUpdateEnabled={!hasPolicyOverride}
      showRoles
      inheritFrom={!!group}
      policyId={policyID}
      policyName={policyName}
      isUpdatingAccess={updateMutation.isLoading}
      isLoading={isLoading}
    />
  );

  async function handleRemove(
    selectedAccesses: Array<UserAccessViewModel | TeamAccessViewModel>
  ) {
    // for typesafety, this should never happen
    if (!environment) {
      return;
    }

    try {
      const newUserPolicies = { ...environment.UserAccessPolicies };
      const newTeamPolicies = { ...environment.TeamAccessPolicies };

      selectedAccesses.forEach((access) => {
        if (access.Type === 'user') {
          delete newUserPolicies[access.Id];
        } else {
          delete newTeamPolicies[access.Id];
        }
      });

      await updateMutation.mutateAsync({
        id: environment.Id,
        userAccessPolicies: newUserPolicies,
        teamAccessPolicies: newTeamPolicies,
      });

      notifySuccess('Success', 'Access successfully updated');
      router.stateService.reload();
    } catch (error) {
      notifyError('Failed to update access', error as Error);
    }
  }

  async function handleUpdate(
    updatedUserAccesses: Array<UserAccessViewModel>,
    updatedTeamAccesses: Array<TeamAccessViewModel>
  ) {
    if (!environment) {
      return;
    }

    try {
      const newUserPolicies = { ...environment.UserAccessPolicies };
      const newTeamPolicies = { ...environment.TeamAccessPolicies };

      updatedUserAccesses.forEach((access) => {
        newUserPolicies[access.Id] = { RoleId: access.Role.Id };
      });

      updatedTeamAccesses.forEach((access) => {
        newTeamPolicies[access.Id] = { RoleId: access.Role.Id };
      });

      await updateMutation.mutateAsync({
        id: environment.Id,
        userAccessPolicies: newUserPolicies,
        teamAccessPolicies: newTeamPolicies,
      });

      notifySuccess('Success', 'Access successfully updated');
      router.stateService.reload();
    } catch (error) {
      notifyError('Failed to update access', error as Error);
    }
  }
}
